import { useRef, useState } from "react";
import { toast } from "sonner";
import { http } from "@/lib/api";
import {
  Camera, X, Sparkle, Warning, ArrowsClockwise, MapPin, Leaf,
} from "@phosphor-icons/react";
import { useAuth } from "@/contexts/AuthContext";

/**
 * PhotoIdentify — photo-based crop / pest / disease / weed / insect
 * identifier. Uploads to /api/nature/ai/analyze (Gemini vision) and shows
 * a labelled probable-identification card with confidence + tags.
 *
 * Two visible modes:
 *   variant="chip"   — a compact button that opens the modal (default).
 *   variant="inline" — the full flow inlined into a page section.
 *
 * The endpoint requires auth (401 otherwise). Anonymous users see a
 * "sign in to analyze" prompt.
 */
const CATEGORY_HINTS = [
  { key: "plant",    label: "🌱 Plant / leaf",  hint: "nature" },
  { key: "pest",     label: "🐛 Pest / insect", hint: "nature" },
  { key: "disease",  label: "🦠 Disease",       hint: "nature" },
  { key: "weed",     label: "🌾 Weed",          hint: "nature" },
  { key: "fruit",    label: "🍎 Fruit",         hint: "nature" },
  { key: "soil",     label: "🟤 Soil",          hint: "nature" },
];

export default function PhotoIdentify({ variant = "chip", locationContext = null, testid = "photo-identify" }) {
  const { user, requireSignIn } = useAuth();
  const [open, setOpen] = useState(variant === "inline");
  const [file, setFile] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [hint, setHint] = useState("plant");
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const inputRef = useRef(null);

  const pickFile = (f) => {
    if (!f) return;
    if (f.size > 8 * 1024 * 1024) { toast.error("Photo is over 8MB — please pick a smaller one"); return; }
    setFile(f);
    setPreviewUrl(URL.createObjectURL(f));
    setResult(null);
  };

  const reset = () => {
    setFile(null);
    setPreviewUrl(null);
    setResult(null);
    if (inputRef.current) inputRef.current.value = "";
  };

  const analyse = async () => {
    if (!user) return requireSignIn("analyse a photo");
    if (!file) { toast.error("Choose a photo first"); return; }
    setBusy(true);
    try {
      const b64 = await fileToBase64(file);
      const hintObj = CATEGORY_HINTS.find((h) => h.key === hint);
      const { data } = await http.post("/nature/ai/analyze", {
        image_base64: b64,
        category_hint: hintObj?.hint || "nature",
        location_context: locationContext || undefined,
      });
      setResult(data);
    } catch (e) {
      const status = e?.response?.status;
      if (status === 401) return requireSignIn("analyse a photo");
      const raw = e?.response?.data?.detail;
      // Only trust backend-authored 4xx JSON detail. 5xx bodies (Cloudflare
      // RFC7807 JSON, HTML error pages) always fall back to the canned copy.
      const friendly = (status && status < 500 && typeof raw === "string" && raw.length < 200 && !raw.startsWith("<"))
        ? raw
        : "Vision analysis is temporarily unavailable — please try again in a moment.";
      toast.error(friendly);
    } finally {
      setBusy(false);
    }
  };

  const trigger = () => {
    if (variant === "chip") setOpen(true);
    setTimeout(() => inputRef.current?.click(), 60);
  };

  if (variant === "chip" && !open) {
    return (
      <button
        onClick={trigger}
        data-testid={`${testid}-open`}
        className="inline-flex items-center gap-1.5 rounded-full bg-[#2D8659] text-white px-4 py-2 font-mono-tech text-[10px] tracking-[0.28em] uppercase hover:bg-[#0F3D2A]"
      >
        <Camera size={11} weight="fill" /> Analyze a photo
      </button>
    );
  }

  const inner = (
    <div className="space-y-4" data-testid={testid}>
      {/* Category hint chips */}
      <div>
        <div className="font-mono-tech text-[10px] tracking-[0.28em] uppercase text-[#3A3830]">
          What kind of subject?
        </div>
        <div className="mt-2 flex flex-wrap gap-1.5" data-testid={`${testid}-hint-row`}>
          {CATEGORY_HINTS.map((h) => (
            <button
              key={h.key}
              onClick={() => setHint(h.key)}
              data-testid={`${testid}-hint-${h.key}`}
              className={`rounded-full border px-3 py-1 text-sm transition-colors ${
                hint === h.key
                  ? "bg-[#0F3D2A] text-white border-[#0F3D2A]"
                  : "bg-white text-[#0F3D2A] border-black/15 hover:border-[#2D8659]"
              }`}
            >
              {h.label}
            </button>
          ))}
        </div>
      </div>

      {/* File picker */}
      <div className="rounded-2xl border-2 border-dashed border-[#2D8659]/30 bg-white p-4">
        <input
          ref={inputRef}
          type="file"
          accept="image/*"
          capture="environment"
          onChange={(e) => pickFile(e.target.files?.[0])}
          data-testid={`${testid}-file`}
          className="hidden"
        />
        {!previewUrl ? (
          <div className="flex flex-col items-center gap-3 py-4">
            <Camera size={32} weight="fill" className="text-[#2D8659]" />
            <div className="text-center">
              <div className="font-serif-display text-lg text-[#151312]">Take a photo — or choose one</div>
              <div className="mt-0.5 text-xs text-[#3A3830]">Leaf · Fruit · Insect · Disease · Weed · Soil</div>
            </div>
            <button
              onClick={() => inputRef.current?.click()}
              data-testid={`${testid}-take`}
              className="inline-flex items-center gap-1.5 rounded-full bg-[#0F3D2A] text-white px-4 py-2 font-mono-tech text-[10px] tracking-[0.28em] uppercase hover:bg-[#2D8659]"
            >
              <Camera size={11} weight="fill" /> Take / choose photo
            </button>
          </div>
        ) : (
          <div className="grid md:grid-cols-[220px_1fr] gap-4 items-start">
            <img
              src={previewUrl}
              alt="Photo preview"
              className="w-full h-[220px] object-cover rounded-xl border border-black/10"
              data-testid={`${testid}-preview`}
            />
            <div className="space-y-2">
              <div className="text-xs text-[#3A3830]">
                {file?.name} · {file.size < 10240 ? `${(file.size / 1024).toFixed(1)} KB` : `${(file.size / 1024 / 1024).toFixed(2)} MB`}
              </div>
              <div className="flex flex-wrap gap-2">
                <button
                  onClick={analyse}
                  disabled={busy}
                  data-testid={`${testid}-analyze`}
                  className="inline-flex items-center gap-1.5 rounded-full bg-[#2D8659] text-white px-4 py-2 font-mono-tech text-[10px] tracking-[0.28em] uppercase hover:bg-[#0F3D2A] disabled:opacity-50"
                >
                  <Sparkle size={11} weight="fill" /> {busy ? "Analyzing…" : "Analyze photo"}
                </button>
                <button
                  onClick={reset}
                  disabled={busy}
                  data-testid={`${testid}-reset`}
                  className="inline-flex items-center gap-1.5 rounded-full border border-black/15 bg-white px-3 py-2 font-mono-tech text-[10px] tracking-[0.28em] uppercase text-[#0F3D2A] hover:bg-black/5"
                >
                  <ArrowsClockwise size={11} weight="bold" /> Change photo
                </button>
              </div>
              {locationContext && (
                <div className="text-[11px] text-[#3A3830] flex items-center gap-1">
                  <MapPin size={10} weight="fill" /> Grounding on {locationContext}
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Result card */}
      {result && (
        <div
          className="rounded-2xl border border-[#2D8659]/30 bg-[#F5EBDC] p-4"
          data-testid={`${testid}-result`}
        >
          <div className="font-mono-tech text-[10px] tracking-[0.28em] uppercase text-[#2D8659] flex items-center gap-1">
            <Leaf size={12} weight="fill" /> Probable identification
          </div>
          <div className="mt-1 font-serif-display text-2xl leading-tight text-[#151312]" data-testid={`${testid}-result-title`}>
            {result.common_name || result.title || "Unknown"}
          </div>
          {result.scientific_name && (
            <div className="mt-0.5 italic text-sm text-[#3A3830]">{result.scientific_name}</div>
          )}
          {result.description && (
            <p className="mt-2 text-sm text-[#151312] leading-relaxed">{result.description}</p>
          )}
          {result.notes && (
            <p className="mt-2 text-xs text-[#3A3830]">{result.notes}</p>
          )}
          <div className="mt-3 flex flex-wrap items-center gap-1.5">
            {typeof result.confidence === "number" && (
              <span
                data-testid={`${testid}-confidence`}
                className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 font-mono-tech text-[9px] tracking-[0.22em] uppercase border ${
                  result.confidence >= 0.75
                    ? "bg-emerald-50 border-emerald-200 text-emerald-800"
                    : result.confidence >= 0.5
                    ? "bg-amber-50 border-amber-200 text-amber-800"
                    : "bg-stone-50 border-stone-200 text-stone-700"
                }`}
              >
                {Math.round(result.confidence * 100)}% confidence
              </span>
            )}
            {(result.tags || []).map((t) => (
              <span key={t} className="inline-flex items-center rounded-full bg-white border border-black/15 px-2 py-0.5 font-mono-tech text-[9px] tracking-[0.22em] uppercase">
                {t}
              </span>
            ))}
          </div>
          <div className="mt-3 rounded-lg border border-amber-200 bg-amber-50 text-amber-900 p-2 text-[11px] flex items-start gap-1.5">
            <Warning size={11} weight="fill" className="mt-0.5" />
            <span>AI interpretation only. Verify with an agronomist or extension officer before spraying or making high-stakes decisions.</span>
          </div>
        </div>
      )}
    </div>
  );

  if (variant === "inline") {
    return inner;
  }

  // Modal wrapper for chip variant
  return (
    <div
      className="fixed inset-0 z-[85] bg-black/50 backdrop-blur-sm flex items-start justify-center px-4 py-16 overflow-y-auto"
      onClick={(e) => e.target === e.currentTarget && setOpen(false)}
      data-testid={`${testid}-modal`}
    >
      <div className="w-full max-w-2xl bg-[#F5EBDC] rounded-3xl border border-black/10 shadow-2xl overflow-hidden">
        <div className="bg-[#0F3D2A] text-[#F8F4E9] px-5 py-3 flex items-center gap-2">
          <Camera size={14} weight="fill" />
          <div className="flex-1 font-mono-tech text-[10px] tracking-[0.32em] uppercase">
            Analyze plant photo
          </div>
          <button
            onClick={() => { setOpen(false); reset(); }}
            data-testid={`${testid}-close`}
            className="h-7 w-7 rounded-full hover:bg-[#F8F4E9]/10 flex items-center justify-center"
          >
            <X size={12} weight="bold" />
          </button>
        </div>
        <div className="p-5">{inner}</div>
      </div>
    </div>
  );
}

function fileToBase64(file) {
  return new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => resolve(String(r.result).split(",")[1] || "");
    r.onerror = reject;
    r.readAsDataURL(file);
  });
}
